import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { AccountInvoice, OrderAccount } from '../../../types/order/OrderAccount';
import Status from '../../atoms/Status/Status';
import { formatShortDate } from '../../../services/helpers/dateTimeFormats';
import Money from '../../atoms/Money/Money';
import InvoiceState from '../../../types/invoice/InvoiceState';
import InvoiceStatus from '../../../types/invoice/InvoiceStatus';
import StatusTypes from '../../../types/StatusTypes';
import NotificationState from '../../atoms/NotificationState/NotificationState';
import { paymentStatus } from '../../../services/helpers/paymentStatus';

interface Props {
    accounts: OrderAccount[];
}

const getAccountInvoices = (account: OrderAccount): AccountInvoice[] => {
    return account.invoices?.length > 0
        ? account.invoices
        : account.currentInvoice
        ? [account.currentInvoice]
        : [];
};

const getVisibleInvoices = (account: OrderAccount): AccountInvoice[] => {
    const visibleFromInvoices = (account.invoices ?? [])
        .filter(invoice => !invoice.canceled)
        .filter(invoice => invoice.invoiceState !== InvoiceState.CreditNote);

    if (visibleFromInvoices.length > 0) {
        return visibleFromInvoices;
    }

    return account.currentInvoice &&
        !account.currentInvoice.canceled &&
        account.currentInvoice.invoiceState !== InvoiceState.CreditNote
        ? [account.currentInvoice]
        : [];
};

const getUnpaidInvoices = (account: OrderAccount): AccountInvoice[] => {
    return getAccountInvoices(account)
        .filter(
            invoice =>
                invoice.paymentStatus === InvoiceStatus.NotPaid ||
                invoice.paymentStatus === InvoiceStatus.PartiallyPaid
        )
        .filter(invoice => !invoice.canceled)
        .filter(invoice => invoice.invoiceState !== InvoiceState.CreditNote);
};

const InvoiceList: React.FC<Props> = ({ accounts }: Props) => {
    const renderDueDate = (currentInvoice: AccountInvoice) => {
        if (currentInvoice.invoiceState === InvoiceState.CreditNote) {
            return null;
        }

        if (!currentInvoice.dueDate) {
            return <DueDate>Due date was not found</DueDate>;
        }

        return <DueDate>Due {formatShortDate(new Date(currentInvoice.dueDate))}</DueDate>;
    };

    const renderInvoiceStatus = (currentInvoice: AccountInvoice) => {
        return currentInvoice.invoiceState !== InvoiceState.CreditNote ? (
            <Status
                type={StatusTypes.Invoice}
                status={paymentStatus(currentInvoice.paymentStatus, currentInvoice.canceled)}
            />
        ) : (
            <Tag>Credit Note</Tag>
        );
    };

    const renderNotificationState = (currentInvoice: AccountInvoice) => {
        switch (currentInvoice.invoiceState) {
            case InvoiceState.Reminder:
            case InvoiceState.FinalReminder:
            case InvoiceState.Void:
                return (
                    <NotificationState
                        state={currentInvoice.invoiceState}
                        canceled={currentInvoice.canceled}
                    />
                );
        }
    };

    const accountsWithMultipleUnpaid = accounts.filter(
        account => getUnpaidInvoices(account).length > 1
    );
    const accountsWithSingleOrNoUnpaid = accounts.filter(
        account => getUnpaidInvoices(account).length <= 1
    );

    const sortedSingleAccounts = [...accountsWithSingleOrNoUnpaid].sort(
        (a, b) =>
            new Date(b.currentInvoice.createdAt).getTime() -
            new Date(a.currentInvoice.createdAt).getTime()
    );

    const allSingleInvoices = sortedSingleAccounts.flatMap(account => getVisibleInvoices(account));

    const nonCanceledInvoices = allSingleInvoices.filter(
        invoice => !invoice.canceled && invoice.paymentStatus !== InvoiceStatus.Canceled
    );

    const hasOnlyFullyPaidInvoices =
        nonCanceledInvoices.length > 0 &&
        nonCanceledInvoices.every(invoice => invoice.paymentStatus === InvoiceStatus.FullyPaid);

    const hasUnpaidInvoices = nonCanceledInvoices.some(
        invoice =>
            invoice.paymentStatus === InvoiceStatus.NotPaid ||
            invoice.paymentStatus === InvoiceStatus.PartiallyPaid
    );

    const renderMultipleUnpaidAccount = (account: OrderAccount) => {
        const unpaidInvoices = getUnpaidInvoices(account).sort(
            (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        );

        if (unpaidInvoices.length === 0) {
            return null;
        }

        return (
            <GroupedContainerLink key={account.accountId} to={`/accounts/${account.accountId}`}>
                <GroupedHeaderRow>
                    <GroupedHeader>Activation {account.purchaseIdentifier}</GroupedHeader>
                    <GroupedTotalAmount>
                        <Money>{unpaidInvoices[0].totalAmount}</Money>
                    </GroupedTotalAmount>
                </GroupedHeaderRow>
                {unpaidInvoices.map(invoice => (
                    <GroupedInvoiceWrapper key={`${account.accountId}-${invoice.invoiceNumber}`}>
                        {renderDueDate(invoice)}
                        <GroupedStatusRow>
                            {renderInvoiceStatus(invoice)}
                            {renderNotificationState(invoice)}
                        </GroupedStatusRow>
                    </GroupedInvoiceWrapper>
                ))}
            </GroupedContainerLink>
        );
    };

    return (
        <>
            {accountsWithMultipleUnpaid.map(account => renderMultipleUnpaidAccount(account))}
            {sortedSingleAccounts.map((account: OrderAccount) => {
                const sortedInvoices = [...getVisibleInvoices(account)].sort(
                    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                );

                const fullyPaidInvoices =
                    account.currentInvoice &&
                    account.currentInvoice.invoiceState !== InvoiceState.CreditNote
                        ? [account.currentInvoice]
                        : sortedInvoices.slice(0, 1);

                const filteredInvoices = hasOnlyFullyPaidInvoices
                    ? fullyPaidInvoices
                    : hasUnpaidInvoices
                    ? sortedInvoices.filter(
                          invoice =>
                              invoice.paymentStatus === InvoiceStatus.NotPaid ||
                              invoice.paymentStatus === InvoiceStatus.PartiallyPaid
                      )
                    : sortedInvoices;

                return filteredInvoices.map((invoice: AccountInvoice) => (
                    <InvoiceBox
                        key={`${account.accountId}-${invoice.invoiceNumber}`}
                        to={`/accounts/${account.accountId}`}
                    >
                        <InvoiceLeftArea>
                            {account.moved && <MovedLabel>Moved</MovedLabel>}
                            <Label>Activation {account.purchaseIdentifier}</Label>
                            {renderDueDate(invoice)}
                            <StatusContainer>
                                {renderInvoiceStatus(invoice)}
                                {renderNotificationState(invoice)}
                            </StatusContainer>
                        </InvoiceLeftArea>
                        <InvoiceAmount>
                            <Money>{invoice.totalAmount}</Money>
                        </InvoiceAmount>
                    </InvoiceBox>
                ));
            })}
        </>
    );
};

const Label = styled.label`
    line-height: 2.5rem;
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    display: block;
`;

const MovedLabel = styled.div`
    line-height: 2.5rem;
    font-size: ${props => props.theme.text.size.large};
    color: ${props => props.theme.colors.text.secondary};
    display: block;
`;

const InvoiceBox = styled(Link)`
    text-decoration: none;
    color: inherit;
    padding: 2rem;
    margin: 0 -2rem;
    display: grid;
    grid-template-areas: 'left right' 'bottom .';
    border-top: 0.1rem solid ${props => props.theme.colors.border};
    &:hover {
        cursor: pointer;
        background-color: ${props => props.theme.colors.tag.default};
    }
`;

const InvoiceLeftArea = styled.div`
    grid-area: left;
`;

const InvoiceAmount = styled.div`
    grid-area: right;
    align-self: center;
    align-items: center;
    text-align: right;
`;

const StatusContainer = styled.div`
    display: flex;
    font-size: ${props => props.theme.text.size.medium};
    flex-wrap: wrap;
    gap: ${props => props.theme.layout.gap.xxsmall};
`;

const DueDate = styled.div`
    margin-bottom: 0.5rem;
    font-family: ${props => props.theme.text.font.bold};
`;

const Tag = styled.div`
    display: inline-block;
    background-color: ${props => props.theme.colors.tag.default};
    padding: 0.5rem 1rem;
    border-radius: 5rem;
    margin: 0.3rem 0.3rem 0 0;
`;

const GroupedContainerLink = styled(Link)`
    display: block;
    text-decoration: none;
    color: inherit;
    border-top: 0.1rem solid ${props => props.theme.colors.border};
    padding: 1rem 2rem;
    margin: 0 -2rem;

    &:hover {
        cursor: pointer;
        background-color: ${props => props.theme.colors.tag.default};
    }
`;

const GroupedHeader = styled.label`
    line-height: 2.5rem;
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    display: block;
`;

const GroupedHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`;

const GroupedTotalAmount = styled.div`
    font-size: ${props => props.theme.text.size.medium};
`;

const GroupedInvoiceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 0;
    border-bottom: 0.1rem solid ${props => props.theme.colors.border};

    &:last-child {
        border-bottom: none;
    }
`;

const GroupedStatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.layout.gap.xxsmall};
`;

export default InvoiceList;
